import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Plus, Search, Pencil, Trash2, Loader2 } from "lucide-react";
import { useI18n } from "../i18n";
import { MODULES } from "../modules";
import api from "../api";
import { PageHeader, StatCard, Card, EmptyState, ProgressBar } from "./ui";
import { SeverityBadge, StatusBadge, TrendIcon } from "../components/SeverityBadge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";

export default function DomainPage({ moduleKey }) {
  const cfg = MODULES[moduleKey];
  const { t } = useI18n();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.list(cfg.entity, { limit: 2000 });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast({ title: "Failed to load data" });
    } finally {
      setLoading(false);
    }
  }, [cfg.entity, toast]);

  useEffect(() => { load(); }, [load]);

  const Icon = cfg.icon;

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((it) => JSON.stringify(it).toLowerCase().includes(q));
  }, [items, search]);

  const statValue = (s) => {
    if (s.type === "count") return items.length;
    if (s.type === "eq") return items.filter((it) => it[s.key] === s.val).length;
    return 0;
  };
  const statLabel = (s) => {
    const known = ["total", "critical", "open"];
    return known.includes(s.label) ? t(s.label) : s.label;
  };

  const openAdd = () => {
    const blank = {};
    cfg.fields.forEach((f) => { if (f.key !== "id") blank[f.key] = ""; });
    setEditing(null); setForm(blank); setOpen(true);
  };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };

  const save = async () => {
    const primary = cfg.fields.find((f) => f.primary)?.key;
    if (primary && !String(form[primary] || "").trim()) {
      toast({ title: "Please fill the required field" });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.update(cfg.entity, editing.id, form);
        toast({ title: "Updated" });
      } else {
        await api.create(cfg.entity, form);
        toast({ title: "Created" });
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast({ title: "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    try {
      await api.remove(cfg.entity, it.id);
      toast({ title: "Deleted" });
      setItems((prev) => prev.filter((x) => x.id !== it.id));
    } catch (e) {
      toast({ title: "Delete failed" });
    }
  };

  const renderCell = (f, it) => {
    const val = it[f.key];
    if (f.badge === "severity") return <SeverityBadge level={val} />;
    if (f.badge === "status") return <StatusBadge status={val} />;
    if (f.badge === "trend") return <TrendIcon trend={val} />;
    if (f.badge === "progress") return <ProgressBar value={val} />;
    if (f.badge === "date") return <span className="text-slate-500 text-xs">{val ? new Date(val).toLocaleString() : "-"}</span>;
    if (f.primary) return <span className="font-medium text-slate-800">{val}</span>;
    return <span className="text-slate-600">{val ?? "-"}</span>;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t(cfg.labelKey)}
        action={!cfg.readOnly && (
          <Button onClick={openAdd} className="bg-teal-700 hover:bg-teal-800"><Plus className="w-4 h-4 me-1.5" /> {t("add")}</Button>
        )}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cfg.stats.map((s, i) => (
          <StatCard key={i} icon={Icon} label={statLabel(s)} value={statValue(s)} tone={s.tone || "teal"} />
        ))}
      </div>

      <Card className="p-4">
        <div className="relative mb-4 max-w-md">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="w-full ps-10 pe-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:bg-white" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-start text-slate-500 border-b border-slate-100">
                  {cfg.fields.map((f) => <th key={f.key} className="py-3 px-3 font-medium text-start">{f.labelKey ? t(f.labelKey) : f.label}</th>)}
                  {!cfg.readOnly && <th className="py-3 px-3 font-medium text-end">{t("actions")}</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 300).map((it) => (
                  <tr key={it.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                    {cfg.fields.map((f) => <td key={f.key} className="py-3 px-3">{renderCell(f, it)}</td>)}
                    {!cfg.readOnly && (
                      <td className="py-3 px-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEdit(it)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => remove(it)} className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && <EmptyState icon={Icon} title={t("noItems")} />}
            {filtered.length > 300 && <p className="text-center text-xs text-slate-400 mt-3">Showing first 300 of {filtered.length} records</p>}
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? t("edit") : t("add")} · {t(cfg.labelKey)}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {cfg.fields.filter((f) => f.badge !== "date").map((f) => (
              <div key={f.key}>
                <Label>{f.labelKey ? t(f.labelKey) : f.label}</Label>
                <Input
                  type={["progress", "number"].includes(f.badge) ? "number" : "text"}
                  value={form[f.key] ?? ""}
                  onChange={(e) => setForm({ ...form, [f.key]: ["progress", "number"].includes(f.badge) ? Number(e.target.value) : e.target.value })}
                  className="mt-1.5"
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
